import { ReactNode } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { config } from '@/config';
import { Platform } from '@/types/api/platforms';
import { getDateString } from '@/utils/dateUtils/dateFormatUtils';
import { AudienceBanner } from './AudienceBanner';

const STORAGE_PREFIX = 'audience-banner-dismissed-';

const formatList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
};

interface PlatformWithEarliestData {
  platform: Platform;
  earliestDataDate: string;
}

interface BannerInputs {
  isPending: boolean;
  enabledBanners: string[];
  showId: string;
  networkId?: string;
  platformsWithoutData: Platform[];
  isOnMarketplace?: boolean;
  hasMarketplacePodcastProfile?: boolean;
  hasPhylloConfigured?: boolean;
  platformsBeforeEarliestData?: PlatformWithEarliestData[];
  reauthRequiredPlatforms?: Platform[];
  /** UTC ISO string of the latest expires_at across all expired platforms. */
  reauthRequiredLatestExpiry?: string;
}

/**
 * Decides which Audience banner (if any) to show and renders it.
 * Highest priority first: not-on-marketplace (no analytics can exist until the
 * show joins the marketplace) wins over data-coming-soon. Returns null when no
 * banner's criteria are met.
 *
 * The `key` ties the banner to its variant + show so it remounts when either
 * changes, resetting the dismissed state read from localStorage.
 */
export const renderAudienceBanner = ({
  isPending,
  enabledBanners,
  showId,
  networkId,
  platformsWithoutData,
  isOnMarketplace,
  hasMarketplacePodcastProfile,
  hasPhylloConfigured,
  platformsBeforeEarliestData = [],
  reauthRequiredPlatforms = [],
  reauthRequiredLatestExpiry,
}: BannerInputs): ReactNode => {
  if (isPending) {
    return null;
  }

  if (enabledBanners.includes('not-on-marketplace') && isOnMarketplace === false) {
    return (
      <AudienceBanner
        key={`not-on-marketplace-${showId}`}
        severity="warning"
        title="Want to dive into your YouTube, Instagram and TikTok analytics?"
        subtitle="Join the Acast Marketplace to connect external channels and gain deeper insights across platforms."
        cta={{
          text: 'Join Marketplace',
          href: `${config.cmsUrl}/networks/${networkId}/shows/${showId}/marketplace`,
        }}
        testId="not-on-marketplace"
        storageKey={`${STORAGE_PREFIX}not-on-marketplace-${showId}`}
      />
    );
  }

  if (enabledBanners.includes('no-profile') && hasMarketplacePodcastProfile === false) {
    return (
      <AudienceBanner
        key={`no-profile-${showId}`}
        severity="warning"
        title="Want to dive into your YouTube, Instagram and TikTok analytics?"
        subtitle="Create your Marketplace profile to link external channels and gain deeper insights across platforms."
        cta={{
          text: 'Add profile',
          href: `${config.cmsUrl}/networks/${networkId}/shows/${showId}/marketplace`,
        }}
        testId="no-profile"
        storageKey={`${STORAGE_PREFIX}no-profile-${showId}`}
      />
    );
  }

  if (
    enabledBanners.includes('no-accounts') &&
    hasMarketplacePodcastProfile === true &&
    hasPhylloConfigured === false
  ) {
    return (
      <AudienceBanner
        key={`no-accounts-${showId}`}
        severity="warning"
        title="Want to dive into your YouTube, Instagram and TikTok analytics?"
        subtitle="Link your accounts to your Marketplace profile to gain deeper insights across channels."
        cta={{
          text: 'Add accounts',
          href: `${config.cmsUrl}/networks/${networkId}/shows/${showId}/marketplace`,
        }}
        testId="no-accounts"
        storageKey={`${STORAGE_PREFIX}no-accounts-${showId}`}
      />
    );
  }

  if (enabledBanners.includes('no-data-for-period') && platformsBeforeEarliestData.length > 0) {
    const platformNames = platformsBeforeEarliestData.map((p) => p.platform);
    const platformList = formatList(platformNames);
    // Latest date across all affected platforms: data is available from that date onward for all.
    const latestEarliestDate = platformsBeforeEarliestData
      .map((p) => p.earliestDataDate)
      .sort()
      .at(-1)!;
    const formattedDate = getDateString(latestEarliestDate);
    const accountPhrase = platformsBeforeEarliestData.length === 1 ? 'account' : 'accounts';
    const subtitle =
      platformsBeforeEarliestData.length === 1
        ? `${platformList} analytics are available from ${formattedDate} onward. Data prior to the date when you connected your account cannot be displayed.`
        : `Analytics are available from ${formattedDate} onward. Data prior to the date when you connected your ${accountPhrase} cannot be displayed.`;
    return (
      <AudienceBanner
        key={`no-data-for-period-${showId}`}
        severity="error"
        title={`No ${platformList} data for this period`}
        subtitle={subtitle}
        testId="no-data-for-period"
        storageKey={`${STORAGE_PREFIX}no-data-for-period-${showId}`}
      />
    );
  }

  if (enabledBanners.includes('reauth-required') && reauthRequiredPlatforms.length > 0) {
    const platformList = formatList(reauthRequiredPlatforms);
    const expiredPhrase =
      reauthRequiredPlatforms.length === 1 ? 'connection has expired' : 'connections have expired';
    return (
      <AudienceBanner
        key={`reauth-required-${showId}`}
        severity="error"
        title={`Your ${platformList} ${expiredPhrase}`}
        subtitle={`Please re-authenticate in your Marketplace profile to access ${platformList} analytics.`}
        cta={{
          text: 'Reconnect',
          href: `${config.cmsUrl}/networks/${networkId}/shows/${showId}/marketplace`,
        }}
        testId="reauth-required"
        storageKey={`${STORAGE_PREFIX}reauth-required-${showId}`}
        validAfter={reauthRequiredLatestExpiry}
      />
    );
  }

  if (enabledBanners.includes('data-coming-soon') && platformsWithoutData.length > 0) {
    return (
      <AudienceBanner
        key={`data-coming-soon-${showId}`}
        severity="warning"
        icon={ClockCircleOutlined}
        title={`Your ${formatList(platformsWithoutData)} data will be available soon.`}
        subtitle="Analytics will appear here once your account syncs. This might take up to 48 hours."
        testId="data-coming-soon"
        storageKey={`${STORAGE_PREFIX}${showId}`}
      />
    );
  }

  return null;
};
